import { MirrorsGame } from './MirrorsGame.js';
import { MirrorsRenderer } from './MirrorsRenderer.js';
import { MirrorType } from './MirrorsGame.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Position } from '../../core/GridHitDetector.js';
import { Direction } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, TraceStep } from '../../input/KeyboardController.js';

export class MirrorsInput extends BaseInput<MirrorsGame, MirrorsRenderer> implements KeyboardControllable {
    private hoveredCell: Position | null = null;
    private gateMode: boolean = false;
    /** Beam travel direction along the current trace, or null before it anchors. */
    private traceDir: Direction | null = null;

    constructor(
        game: MirrorsGame,
        renderer: MirrorsRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
        this.canvas.addEventListener('mousemove', this.handleMouseMove);
        this.canvas.addEventListener('mouseleave', this.handleMouseLeave);
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
        this.canvas.removeEventListener('mousemove', this.handleMouseMove);
        this.canvas.removeEventListener('mouseleave', this.handleMouseLeave);
    }

    private handleClick = (event: MouseEvent): void => {
        const { x, y } = this.getCoordinates(event);
        const hit = this.detector.detectCell(x, y);

        if (!hit) return;

        if (this.gateMode && isVortexGame(this.game)) {
            if (this.game.toggleVortexGate(hit)) {
                this.onUpdate();
            }
        } else {
            this.game.cycleMirror(hit);
            this.onUpdate();
        }
    };

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const hit = this.detector.detectCell(x, y);

        if (hit && this.game.getMirrorAt(hit) !== null) {
            this.game.setMirror(hit, null);
            this.onUpdate();
        }
    };

    private handleMouseMove = (event: MouseEvent): void => {
        const { x, y } = this.getCoordinates(event);
        const hit = this.detector.detectCell(x, y);

        if (hit && (!this.hoveredCell ||
            hit.row !== this.hoveredCell.row ||
            hit.col !== this.hoveredCell.col)) {
            this.hoveredCell = hit;
            // Could trigger hover highlight here if desired
        }
    };

    private handleMouseLeave = (): void => {
        this.hoveredCell = null;
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Mirror the click semantics: place = cycle (/ → \ → empty), delete = remove.

    placeAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        if (this.game.getGrid().hasDigitAt(pos)) return false;
        this.game.cycleMirror(pos);
        return true;
    }

    deleteAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        if (this.game.getMirrorAt(pos) === null) return false;
        this.game.setMirror(pos, null);
        return true;
    }

    /** The mirror that turns a beam travelling `fromDir` into `moveDir`. Only
     *  perpendicular turns have one (straight/reversed moves map to undefined). */
    private static readonly TURN_MIRROR: Record<string, MirrorType> = {
        'E,N': '/', 'N,E': '/', 'W,S': '/', 'S,W': '/',
        'E,S': '\\', 'S,E': '\\', 'W,N': '\\', 'N,W': '\\',
    };

    /** Each trace step lays a mirror along the beam, so show the "last step" hint. */
    wantsTraceHint(): boolean {
        return true;
    }

    /** Anchor the trace to the beam's travel direction through the start cell. We
     *  don't anchor on a mirror cell: there the beam bends, so its entry and exit
     *  directions differ and "trace straight from here" would be ambiguous. */
    traceStart(pos: Position): void {
        this.traceDir = this.beamDirThrough(pos);
    }

    /** Trace step at the pivot the cursor just left (`from`), relative to the beam
     *  direction so far:
     *   - a turn (perpendicular): place the mirror that bends the beam that way;
     *   - a straight pass-through: clear any mirror so the beam runs straight;
     *   - a reversal (180°): leave the board untouched.
     *  The move direction then becomes the running trace direction. */
    traceStep({ from, moveDir }: TraceStep): boolean {
        const changed = this.traceDir !== null && this.applyTraceMirror(from, this.traceDir, moveDir);
        this.traceDir = moveDir;
        return changed;
    }

    private beamDirThrough(pos: Position): Direction | null {
        if (this.game.getMirrorAt(pos) !== null) return null;
        for (const seg of this.game.traceLaser().segments) {
            if (seg.from.row === pos.row && seg.from.col === pos.col) return seg.direction;
        }
        return null;
    }

    private applyTraceMirror(pivot: Position, fromDir: Direction, moveDir: Direction): boolean {
        if (this.gateMode) return false;

        if (fromDir === moveDir) {
            // Traced straight through: remove a mirror in the way, if any.
            if (this.game.getMirrorAt(pivot) === null) return false;
            this.game.setMirror(pivot, null);
            return true;
        }

        const mirror = MirrorsInput.TURN_MIRROR[`${fromDir},${moveDir}`];
        if (!mirror) return false; // reversal: no single mirror bends it
        if (this.game.getGrid().hasDigitAt(pivot)) return false;
        if (this.game.getMirrorAt(pivot) === mirror) return false; // already correct
        this.game.setMirror(pivot, mirror);
        return true;
    }

    /** `X` goes to the top-left cell, where the beam enters. */
    cursorOrigin(): Position | null {
        return { row: 0, col: 0 };
    }
}
